import styled from "@emotion/styled";

const colors = {
  mintPrimary: "#E0F2F1", // 연한 민트색 (헤더 배경)
  mintSecondary: "#B2DFDB", // 살짝 진한 민트색 (호버 효과)
  mintDark: "#004D40", // 진한 민트색 (텍스트)
  pinkLight: "#FCE4EC", // 연한 분홍색
  pinkLighter: "#FFF0F4", // 아주 연한 분홍색 (배경)
  pinkPrimary: "#F8BBD0", // 기본 분홍색
  pinkDark: "#C2185B", // 진한 분홍색
  greenLight: "#F1F8E9", // 연한 녹색 (테이블 배경)
  greenHover: "#DCEDC8", // 호버시 녹색
  textPrimary: "#2E7D32", // 텍스트 주 색상
  textSecondary: "#558B2F", // 텍스트 보조 색상
};

export const Container = styled.div`
  padding: 24px;
  background-color: ${colors.mintSecondary}; /* 변경: 배경색을 민트 계열로 */
  min-height: calc(100vh - 200px);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background-color: ${colors.mintPrimary};
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const Title = styled.h1`
  font-size: 24px;
  color: ${colors.mintDark};
  margin: 0;
`;

export const AddButton = styled.button`
  background-color: ${colors.mintSecondary};
  color: ${colors.mintDark};
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: ${colors.mintPrimary};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const TableWrapper = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: auto;
  /* 메뉴바 및 추가 헤더 영역을 고려하여 max-height 조정 */
  max-height: calc(100vh - 250px);
  position: relative;

  thead {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${colors.mintPrimary};
  }

  @media (max-width: 768px) {
    max-height: calc(100vh - 200px);
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

export const TableRow = styled.tr`
  &:hover {
    background-color: ${colors.greenHover};
  }
`;

export const TableHeader = styled.th`
  background-color: ${colors.mintPrimary};
  color: ${colors.mintDark};
  font-weight: 600;
  padding: 16px;
  text-align: left;
  border-bottom: 2px solid ${colors.mintSecondary};
  cursor: ${(props) => (props.sortable ? "pointer" : "default")};
  user-select: none;
  position: relative;

  ${(props) =>
    props.sortable &&
    `
    &:after,
    &:before {
      content: '';
      position: absolute;
      right: 8px;
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      opacity: 0.3;
      transition: all 0.2s ease;
    }

    // 위쪽 화살표
    &:before {
      top: calc(50% - 6px);
      border-bottom: 5px solid ${colors.mintDark};
      ${
        props.sortDirection === "asc" &&
        `
        opacity: 1;
        border-bottom-color: #FFB6C1;  // 파스텔톤 빨간색
      `
      }
    }

    // 아래쪽 화살표
    &:after {
      top: calc(50% + 1px);
      border-top: 5px solid ${colors.mintDark};
      ${
        props.sortDirection === "desc" &&
        `
        opacity: 1;
        border-top-color: #FFB6C1;  // 파스텔톤 빨간색
      `
      }
    }

    &:hover {
      background-color: ${colors.mintSecondary};

      &:before,
      &:after {
        opacity: ${props.sortDirection ? "1" : "0.6"};
      }
    }
  `}

  padding-right: ${(props) => (props.sortable ? "32px" : "16px")};
`;

export const TableCell = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
`;

export const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  min-width: 80px;

  ${({ status }) => {
    switch (status) {
      case "Completed":
        return `
          background-color: ${colors.mintPrimary};
          color: ${colors.mintDark};
        `;
      case "In Progress":
        return `
          background-color: ${colors.pinkLight};
          color: ${colors.pinkDark};
        `;
      case "Not Started":
        return `
          background-color: ${colors.greenLight};
          color: ${colors.textSecondary};
        `;
      default:
        return `
          background-color: ${colors.greenLight};
          color: ${colors.textSecondary};
        `;
    }
  }}
`;

export const ActionButton = styled.button`
  padding: 6px 12px;
  margin: 0 4px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${({ edit }) =>
    edit &&
    `
    background-color: ${colors.mintPrimary};
    color: ${colors.mintDark};
    &:hover {
      background-color: ${colors.mintSecondary};
    }
  `}

  ${({ delete: isDelete }) =>
    isDelete &&
    `
    background-color: ${colors.pinkLight};
    color: ${colors.pinkDark};
    &:hover {
      background-color: ${colors.pinkPrimary};
    }
  `}
`;

export const FilterContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  padding: 16px;
  background-color: ${colors.mintPrimary};
  border-radius: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

export const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${colors.mintSecondary};
  border-radius: 4px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: ${colors.mintDark};
  }
`;

export const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${colors.mintSecondary};
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  background-color: white;

  &:focus {
    outline: none;
    border-color: ${colors.mintDark};
  }
`;

export const LoadMoreButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: ${colors.mintPrimary};
  border: none;
  border-top: 1px solid ${colors.mintSecondary};
  color: ${colors.mintDark};
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.mintSecondary};
  }
`;
